import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPiggyBank } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import ChatbotAi from "../components/ChatbotAi";
import '../css/calculatorpages.css';

const PPFCalculator = () => {
    const [investmentAmountYear, setInvestmentAmountYear] = useState('');
    const [durationYears, setDurationYears] = useState('');
    const interestRate = 7.1;
    const [totalInvestment, setTotalInvestment] = useState(null);
    const [interestEarned, setInterestEarned] = useState(null);
    const [maturityValue, setMaturityValue] = useState(null);
    const [isIpadPro, setIsIpadPro] = useState(false);

    const navigate = useNavigate();

    const handleBackClick = () => {
        navigate('/#financial-calculators-section');
    };

    useEffect(() => {
        const checkIpadPro = () => {
            const width = window.innerWidth;
            setIsIpadPro(width === 1024 || width === 1366);
        };

        checkIpadPro();
        window.addEventListener('resize', checkIpadPro);

        return () => {
            window.removeEventListener('resize', checkIpadPro);
        };
    }, []);

    const calculatePPF = () => {
        const A = parseFloat(investmentAmountYear);
        const N = parseFloat(durationYears);
        const R = interestRate / 100;

        if (A >= 500 && A <= 150000 && N > 0) {
            let calculatedMaturityValue = 0;
            let totalInvestedAmount = 0;

            for (let i = 1; i <= N; i++) {
                totalInvestedAmount += A;
                if (i === 1) {
                    calculatedMaturityValue = A * (1 + R);
                } else {
                    calculatedMaturityValue = (calculatedMaturityValue + A) * (1 + R);
                }
            }

            const calculatedInterestEarned = calculatedMaturityValue - totalInvestedAmount;

            setTotalInvestment(totalInvestedAmount.toFixed(2));
            setInterestEarned(calculatedInterestEarned.toFixed(2));
            setMaturityValue(calculatedMaturityValue.toFixed(2));
        } else {
            setTotalInvestment(null);
            setInterestEarned(null);
            setMaturityValue(null);
        }
    };

    const ipadProLastInputStyle = isIpadPro ? {
        marginLeft: '0',
        marginRight: 'auto',
        alignSelf: 'flex-start'
    } : {};

    return (
        <div>
            <Helmet>
                <title>PPF Calculator | Grow Your Savings with Confidence</title>
                <meta name="description" content="Estimate your PPF returns fast and plan your long-term savings smartly. Use our PPF calculator for accurate investment growth predictions." />
                <link rel="canonical" href="https://policysamjho.com/calculators/ppf-calculator/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "PPF Calculator",
                        "description": "Estimate your PPF returns fast and plan your long-term savings smartly. Use our PPF calculator for accurate investment growth predictions.",
                        "url": "https://policysamjho.com/calculators/ppf-calculator/",
                        "applicationCategory": "FinancialApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "INR"
                        },
                        "provider": {
                            "@type": "Organization",
                            "name": "Policy Samjho",
                            "url": "https://policysamjho.com/"
                        }
                    })}
                </script>
            </Helmet>

            <div className="calculator-page-container">
                <button onClick={handleBackClick} className="calculator-back-button">
                    Back
                </button>
                <div className="calculator-content-wrapper">
                    <div className="calculator-info-section">
                        <div className="calculator-header-content">
                            <FontAwesomeIcon icon={faPiggyBank} className="calculator-header-icon" />
                            <h1 className="calculator-title">PPF Calculator </h1>
                        </div>
                        <p className="calculator-description">
                            Public Provident Fund or PPF is one of the most popular tax-saving investment options available in India. It was launched by the government of India to provide guaranteed returns to small-savers. PPF investments fall in the Exempt-Exempt-Exempt category so, the investment amount, the interest earned and the maturity amount are all tax free. As the PPF scheme offers guaranteed returns, you can estimate your returns using a PPF calculator even before you invest. It is an online tool that can help you resolve your queries related to calculation of returns and interest earnings from your Public Provident Fund investments. The maturity amount and interest earned can be easily calculated with the help of a PPF calculator.
                        </p>
                    </div>
                    <div className={`calculator-main-section ${maturityValue === null ? 'no-result' : ''}`}>
                        <div className="calculator-input-section">
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="investmentAmountYear">Investment Amount (Year) (₹500 - ₹1.5 Lakhs)</label>
                                    <input
                                        type="number"
                                        id="investmentAmountYear"
                                        value={investmentAmountYear}
                                        onChange={(e) => setInvestmentAmountYear(e.target.value)}
                                        placeholder="e.g. 100000"
                                    />
                                </div>
                                <div className="input-group">
                                    <label>Rate of Interest</label>
                                    <input
                                        type="text"
                                        value={`${interestRate}% (Fixed)`}
                                        readOnly
                                        style={{
                                            backgroundColor: '#e9ecef',
                                            color: '#495057',
                                            cursor: 'not-allowed',
                                            fontWeight: 'bold',
                                            borderRadius: '4px',
                                            padding: '8px 12px',
                                            border: '1px solid #ced4da',
                                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,.075)'
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="input-group" style={ipadProLastInputStyle}>
                                <label htmlFor="durationYears">Duration (Years)</label>
                                <input
                                    type="number"
                                    id="durationYears"
                                    value={durationYears}
                                    onChange={(e) => setDurationYears(e.target.value)}
                                    placeholder="e.g. 15"
                                />
                            </div>
                            <button onClick={calculatePPF} className="calculate-button">Calculate</button>
                        </div>

                        {maturityValue !== null && (
                            <div className="calculator-result-section">
                                <h2>You Get</h2>
                                <div className="result-item">
                                    <span>Total investment</span>
                                    <span className="result-value">₹{totalInvestment}</span>
                                </div>
                                <div className="result-item">
                                    <span>Interest earned</span>
                                    <span className="result-value">₹{interestEarned}</span>
                                </div>
                                <div className="result-item">
                                    <span>Maturity value</span>
                                    <span className="result-value">₹{maturityValue}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ChatbotAi />
        </div>
    );
};

export default PPFCalculator;